/**
 * Custom checkout — dynamic progress indicator (4 kroki jak sekcje) — scroll-based.
 *
 * Baymard UX rec: progress indicator powinien faktycznie odzwierciedlać gdzie user
 * jest w flow. Hardcoded `.is-current` na step 1 jest mylące — usuwamy je w PHP
 * (default state pozostaje 1) i JS aktualizuje w trakcie scrolla.
 *
 * Mapping sections → steps (B2B: invoice → 2, shipping-address → 3):
 *   - contact            → step 1 "Kontakt"
 *   - address            → step 2 "Adres"
 *   - shipping           → step 3 "Dostawa"
 *   - payment, notes, terms → step 4 "Płatność"
 *
 * Mechanism: IntersectionObserver z `rootMargin: -30% 0px -30% 0px` — sekcja musi
 * być w środkowych 40% viewport żeby liczyć jako "current" (smooth, intentional).
 * Najbardziej widoczna sekcja (highest intersectionRatio) wygrywa.
 */

const SECTION_TO_STEP = {
    contact: 1,
    address: 2,
    invoice: 2,
    'shipping-address': 3,
    shipping: 3,
    payment: 4,
    notes: 4,
    terms: 4,
};

export function initCheckoutProgressScroll() {
    const progress = document.querySelector('[data-hn-co-progress]');
    if (!progress) return;

    const steps = Array.from(progress.querySelectorAll('[data-hn-co-step]'));
    if (steps.length === 0) return;

    // Zgody w przyklejonym podsumowaniu (desktop) są zawsze widoczne, więc nie mogą wyznaczać kroku.
    const sections = Array.from(document.querySelectorAll('[data-hn-co-section]')).filter((s) => !s.closest('[data-hn-co-summary]'));
    if (sections.length === 0) return;

    // Aktualnie najbardziej widoczne section per step (tracking via Map).
    const visibility = new Map(); // section name → intersectionRatio

    const setCurrentStep = (stepNum) => {
        steps.forEach((step) => {
            const sNum = parseInt(step.dataset.hnCoStep, 10);
            step.classList.toggle('is-current', sNum === stepNum);
            if (sNum === stepNum) step.setAttribute('aria-current', 'step');
            else step.removeAttribute('aria-current');
            // `is-done` — CSS (_checkout.scss:752) ma już styling z zielonym tick.
            step.classList.toggle('is-done', sNum < stepNum);
        });
    };

    const recomputeCurrent = () => {
        // Znajdź sekcję z najwyższym intersectionRatio i zmapuj na step.
        let topSection = null;
        let topRatio = -1;
        for (const [name, ratio] of visibility.entries()) {
            if (ratio > topRatio) {
                topRatio = ratio;
                topSection = name;
            }
        }
        if (!topSection) return;
        const step = SECTION_TO_STEP[topSection];
        if (step) setCurrentStep(step);
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            const name = entry.target.dataset.hnCoSection;
            if (!name) return;
            visibility.set(name, entry.isIntersecting ? entry.intersectionRatio : 0);
        });
        recomputeCurrent();
    }, {
        // Środkowe 40% viewport jest "current zone" — sekcja musi być centralnie
        // widoczna żeby liczyć jako aktualnie przeglądana.
        rootMargin: '-30% 0px -30% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
    });

    sections.forEach((section) => observer.observe(section));
}
